import {
    ReactElement,
    useState,
} from 'react';

import {
    Center,
    Checkbox,
    SimpleGrid,
    Table,
    Text,
} from '@mantine/core';

import {useNotificationSubscriptions} from '../hooks/useNotificationSubscriptions.ts';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    SimpleTablePagination,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';

import PropTypes from 'prop-types';
import {INotificationSubscriptionTableColumn, NotificationSubscription} from '../types';
import {useParams} from "react-router-dom";
import {useAuth} from "../../../hooks/useAuth.ts";
import {useVerifiedPermissions} from "../../../hooks/useVerifiedPermissions.ts";
import {columns} from './columns';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/NotificationSubscriptionsService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 extraTools,
                                 settings,
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[],
    selectedRecords: NotificationSubscription[],
    setSelectedRecords: (records: NotificationSubscription[]) => void,
}) => {

    const {allowedActions} = useVerifiedPermissions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<NotificationSubscription | null>(null);

    const {id: productId} = useParams();

    if (!productId) {
        throw new Error('productId is required parameter');
    }

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        handleFilterByKeyword,
    } = useNotificationSubscriptions(productId);

    const handleRecordClick = (record: NotificationSubscription) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: NotificationSubscription) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as NotificationSubscription);
            queryClient.invalidateQueries({queryKey: ['notification-subscriptions']});
        }
    };

    const handleRowSelection = (row: { id: string }) => {
        const exists = selectedRecords.find(r => r.id === row.id);
        if (exists) {
            setSelectedRecords(selectedRecords.filter(r => r.id !== row.id));
        } else {
            const fullRecord = visibleItems.find(r => r.id === row.id);
            if (fullRecord) {
                setSelectedRecords([...selectedRecords, fullRecord]);
            }
        }
    };

    const handleSelectAllRows = () => {
        if (selectedRecords.length === visibleItems.length) {
            setSelectedRecords([]);
        } else {
            setSelectedRecords([...visibleItems]);
        }
    };

    const getColumns = () => {
        let cols: INotificationSubscriptionTableColumn[] = [];
        if (allowedActions.includes('DeleteNotificationSubscription')) {
            cols.push({
                id: 'select',
                title: '',
                render: item => {
                    if (item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername) {
                        return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                    } else {
                        return (<Center><Checkbox disabled={true}></Checkbox></Center>);
                    }
                },
            });
        }

        cols.push({
            title: I18n.get('Endpoint'),
            width: '1fr',
            render: (item: NotificationSubscription) => (
                <Text
                    c="blue"
                    style={{cursor: 'pointer'}}
                    onClick={() => handleRecordClick(item)}
                >
                    {item.endpoint}
                </Text>
            ),
        });

        cols = cols.concat(columns.filter(col => col.title !== I18n.get('Endpoint')));

        return cols;
    };

    const selection: Record<string, NotificationSubscription> = {};
    selectedRecords.forEach(r => {
        selection[r.id] = r;
    });

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={10}
                handleItemsPerPageChange={() => {}}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumns() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={visibleItems}
                    loading={loading}
                    selection={selection}
                    columns={getColumns() as TableBodyColumn[]}
                />
            </Table>

            <SimpleTablePagination
                totalPages={1}
                visibleCount={visibleItems.length}
                handlePageChange={() => {}}
            />

            <FormDrawer
                record={selectedRecord || {} as NotificationSubscription}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};

RecordsTable.propTypes = {
    extraTools: PropTypes.array,
    settings: PropTypes.object,
};
